import { useState, useMemo, useEffect } from 'react'
import { Command } from 'cmdk'
import { useNavigate } from 'react-router-dom'
import {
  Home,
  Library,
  Network,
  LayoutGrid,
  Search,
  FileText,
  Plus,
  Settings,
  ArrowLeft,
  ArrowRight,
  Zap
} from 'lucide-react'
import { useNavigationStore } from '@/stores/navigationStore'
import { useConcepts } from '@/hooks/useConcepts'
import '@/features/shared/CommandBar/command-bar.css'

interface CommandItem {
  id: string
  type: 'navigation' | 'action' | 'search_result' | 'recent'
  label: string
  description?: string
  icon?: React.ReactNode
  shortcut?: string
  onSelect: () => void
}

export function CommandBar() {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const { history, goBack, goForward, canGoBack, canGoForward } = useNavigationStore()
  const { data: concepts } = useConcepts()

  // Filter concepts based on search
  const searchResults = useMemo(() => {
    if (!search.trim() || !concepts) return []
    const query = search.toLowerCase()
    return concepts
      .filter(c =>
        c.name.toLowerCase().includes(query) ||
        c.definition?.toLowerCase().includes(query)
      )
      .slice(0, 8)
  }, [search, concepts])

  // Navigation commands
  const navigationCommands: CommandItem[] = useMemo(() => [
    {
      id: 'nav-pulse',
      type: 'navigation',
      label: 'Ir para Pulse',
      description: 'Dashboard principal',
      shortcut: '⌘1',
      icon: <Home className="w-4 h-4" />,
      onSelect: () => { navigate('/'); setOpen(false) }
    },
    {
      id: 'nav-library',
      type: 'navigation',
      label: 'Ir para Library',
      description: 'Navegador de conhecimento',
      shortcut: '⌘2',
      icon: <Library className="w-4 h-4" />,
      onSelect: () => { navigate('/library'); setOpen(false) }
    },
    {
      id: 'nav-constellation',
      type: 'navigation',
      label: 'Ir para Constellation',
      description: 'Visualização do grafo',
      shortcut: '⌘3',
      icon: <Network className="w-4 h-4" />,
      onSelect: () => { navigate('/constellation'); setOpen(false) }
    },
    {
      id: 'nav-canvas',
      type: 'navigation',
      label: 'Ir para Canvas',
      description: 'Quadro infinito',
      shortcut: '⌘4',
      icon: <LayoutGrid className="w-4 h-4" />,
      onSelect: () => { navigate('/canvas'); setOpen(false) }
    },
    {
      id: 'nav-settings',
      type: 'navigation',
      label: 'Configurações',
      shortcut: '⌘,',
      icon: <Settings className="w-4 h-4" />,
      onSelect: () => { navigate('/settings'); setOpen(false) }
    }
  ], [navigate])

  // Action commands
  const actionCommands: CommandItem[] = useMemo(() => [
    {
      id: 'action-back',
      type: 'action',
      label: 'Voltar',
      shortcut: '⌘[',
      icon: <ArrowLeft className="w-4 h-4" />,
      onSelect: () => { if (canGoBack()) { goBack(); navigate(-1) }; setOpen(false) }
    },
    {
      id: 'action-forward',
      type: 'action',
      label: 'Avançar',
      shortcut: '⌘]',
      icon: <ArrowRight className="w-4 h-4" />,
      onSelect: () => { if (canGoForward()) { goForward(); navigate(1) }; setOpen(false) }
    },
    {
      id: 'action-new-ingest',
      type: 'action',
      label: 'Nova Ingestão',
      description: 'Ingerir novo conteúdo',
      shortcut: '⌘I',
      icon: <Plus className="w-4 h-4" />,
      onSelect: () => { navigate('/'); setOpen(false) }
    },
    {
      id: 'action-search',
      type: 'action',
      label: 'Busca Semântica',
      description: 'Buscar no grafo',
      icon: <Search className="w-4 h-4" />,
      onSelect: () => { navigate('/constellation'); setOpen(false) }
    }
  ], [navigate, goBack, goForward, canGoBack, canGoForward])

  // Recent entries
  const recentCommands: CommandItem[] = useMemo(() =>
    history.slice(-5).reverse().map(entry => ({
      id: `recent-${entry.timestamp}`,
      type: 'recent' as const,
      label: entry.title,
      description: entry.surface,
      icon: <Zap className="w-4 h-4 text-rsb-accent" />,
      onSelect: () => { navigate(`/${entry.surface === 'pulse' ? '' : entry.surface}`); setOpen(false) }
    }))
  , [history, navigate])

  // Search result commands
  const searchCommands: CommandItem[] = useMemo(() =>
    searchResults.map(result => ({
      id: result.id,
      type: 'search_result' as const,
      label: result.name,
      description: result.definition?.slice(0, 60),
      icon: <FileText className="w-4 h-4 text-blue-400" />,
      onSelect: () => { navigate(`/dive/${result.id}`); setOpen(false) }
    }))
  , [searchResults, navigate])

  // Keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(prev => !prev)
      }

      // Quick nav shortcuts
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
        switch (e.key) {
          case '1':
            e.preventDefault()
            navigate('/')
            break
          case '2':
            e.preventDefault()
            navigate('/library')
            break
          case '3':
            e.preventDefault()
            navigate('/constellation')
            break
          case '4':
            e.preventDefault()
            navigate('/canvas')
            break
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Comando global"
      className="command-dialog"
    >
      <div className="command-container">
        <Command.Input
          value={search}
          onValueChange={setSearch}
          placeholder="Buscar ou executar comando..."
          className="command-input"
        />

        <Command.List className="command-list">
          <Command.Empty className="command-empty">
            Nenhum resultado encontrado.
          </Command.Empty>

          {search === '' ? (
            <>
              <Command.Group heading="Navegação" className="command-group">
                {navigationCommands.map(cmd => (
                  <Command.Item
                    key={cmd.id}
                    value={cmd.label}
                    onSelect={cmd.onSelect}
                    className="command-item"
                  >
                    <span className="command-icon">{cmd.icon}</span>
                    <div className="command-content">
                      <span className="command-label">{cmd.label}</span>
                      {cmd.description && (
                        <span className="command-description">{cmd.description}</span>
                      )}
                    </div>
                    {cmd.shortcut && (
                      <span className="command-shortcut">{cmd.shortcut}</span>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>

              <Command.Group heading="Ações" className="command-group">
                {actionCommands.map(cmd => (
                  <Command.Item
                    key={cmd.id}
                    value={cmd.label}
                    onSelect={cmd.onSelect}
                    className="command-item"
                  >
                    <span className="command-icon">{cmd.icon}</span>
                    <div className="command-content">
                      <span className="command-label">{cmd.label}</span>
                      {cmd.description && (
                        <span className="command-description">{cmd.description}</span>
                      )}
                    </div>
                    {cmd.shortcut && (
                      <span className="command-shortcut">{cmd.shortcut}</span>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>

              {recentCommands.length > 0 && (
                <Command.Group heading="Recentes" className="command-group">
                  {recentCommands.map(cmd => (
                    <Command.Item
                      key={cmd.id}
                      value={cmd.label}
                      onSelect={cmd.onSelect}
                      className="command-item"
                    >
                      <span className="command-icon">{cmd.icon}</span>
                      <div className="command-content">
                        <span className="command-label">{cmd.label}</span>
                        <span className="command-description">{cmd.description}</span>
                      </div>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
            </>
          ) : (
            <Command.Group heading="Resultados" className="command-group">
              {searchCommands.map(cmd => (
                <Command.Item
                  key={cmd.id}
                  value={cmd.label}
                  onSelect={cmd.onSelect}
                  className="command-item"
                >
                  <span className="command-icon">{cmd.icon}</span>
                  <div className="command-content">
                    <span className="command-label">{cmd.label}</span>
                    {cmd.description && (
                      <span className="command-description">{cmd.description}</span>
                    )}
                  </div>
                </Command.Item>
              ))}
            </Command.Group>
          )}
        </Command.List>

        <div className="command-footer">
          <span className="command-hint">
            <kbd>↑↓</kbd> navegar
          </span>
          <span className="command-hint">
            <kbd>↵</kbd> selecionar
          </span>
          <span className="command-hint">
            <kbd>esc</kbd> fechar
          </span>
        </div>
      </div>
    </Command.Dialog>
  )
}
